/**
 * Static catalog of Mednet partner clinics that Telomera patients can register at.
 *
 * `city` doubles as the stable identifier: the backend's appointment +
 * invitation APIs accept the Lithuanian display name directly (enum:
 * Vilnius | Kaunas | Šiauliai | Panevėžys | Mažeikiai), so there's no need
 * for a separate slug.
 */
export type MednetCity =
  | "Vilnius"
  | "Kaunas"
  | "Šiauliai"
  | "Panevėžys"
  | "Mažeikiai";

export interface MednetLocation {
  /** City name shown in the UI and sent to the backend. */
  city: MednetCity;
  /** Short address shown next to the city option (e.g. "Santariškių g. 5"). */
  address: string;
  /** Full descriptive address used in the location preview. */
  fullAddress: string;
}

export const MEDNET_LOCATIONS: MednetLocation[] = [
  {
    city: "Vilnius",
    address: "Santariškių g. 5",
    fullAddress: "Santariškių g. 5, Vilnius",
  },
  {
    city: "Kaunas",
    address: "Kęstučio g. 38",
    fullAddress: "Kęstučio g. 38, Kaunas",
  },
  {
    city: "Šiauliai",
    address: "Gumbinės g. 33C",
    fullAddress: "Gumbinės g. 33C, Šiauliai",
  },
  {
    city: "Panevėžys",
    address: "Puzino g. 10",
    fullAddress: "Puzino g. 10, Panevėžys",
  },
  {
    city: "Mažeikiai",
    address: "Naftininkų g. 9",
    fullAddress: "Naftininkų g. 9, Mažeikiai",
  },
];

/**
 * Best-effort match for a free-form hint (e.g. checkout `order.city`, the
 * legacy `visit_registration.location` string, or a Rezus location name like
 * `"Šiauliai, Gumbinės g. 33C"`).
 */
export function findMednetLocation(
  hint?: string | null,
): MednetLocation | undefined {
  if (!hint) return undefined;
  const normalized = hint.toLowerCase();
  return MEDNET_LOCATIONS.find(
    (loc) =>
      normalized.includes(loc.city.toLowerCase()) ||
      normalized.includes(loc.address.toLowerCase()) ||
      normalized.includes(
        loc.address
          .toLowerCase()
          .replace(/\s*g\.\s*/g, " ")
          .trim(),
      ),
  );
}
